import { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { PaymentInformation } from '../../../shared/payment/payment-information';
import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import { lookupCountryQuery } from './id-verification-country-query';
import { Country } from '../../../../types/country';
import {
  IdVerificationProcessPaymentContainerApplicantUserCybersourcePublicKeyIdDocument,
  IdVerificationProcessPaymentContainerIdentityVerificationCaseV1UpdateDocument,
  IdVerificationProcessPaymentContainerIdentityVerificationCaseV1DeleteDocument,
  IdVerificationProcessPaymentContainerApplicantUserDocument,
  ApplicantPaymentInstrumentDisplayContainerApplicantUserPaymentInstrumentDocument,
  PaymentInstrument,
  IdentityVerificationCaseV1UpdateInput,
  IdVerificationProcessPaymentContainerIdentityVerificationCaseV1Document,
  IdVerificationProcessPaymentContainerApplicantUserUpdateDocument,
} from '../../../../generated';
import { useProfileSubmissionCheck } from "../../../shared/use-profile-submission-check";
import { PaymentPollingContainer } from "./payment-polling.container";
import { ComponentQueryLoader } from "../../../shared/component-query-loader";
import { getItem } from "../../../shared/local-storage";

interface IdVerificationProcessPaymentContainerProps {
  applicantUserId: string;
}

export const IdVerificationProcessPaymentContainer: React.FC<IdVerificationProcessPaymentContainerProps> = (props) => {
  const navigate = useNavigate();
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const caseId = getItem("caseId");
  const {
    data: applicantUserData,
    loading: applicantUserLoading,
    error: applicantUserError,
  } = useQuery(IdVerificationProcessPaymentContainerApplicantUserDocument, {
    variables: { 
      applicantUserId: props.applicantUserId
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
  });

  const {
    data: countryData,
    loading: countryLoading,
    error: countryError,
  } = useQuery(lookupCountryQuery);

  const {
    data: cybersourcePublicKeyData,
    loading: cybersourcePublicKeyLoading,
    error: cybersourcePublicKeyError,
  } = useQuery(IdVerificationProcessPaymentContainerApplicantUserCybersourcePublicKeyIdDocument);

  const {
    data: paymentInstrumentData,
    loading: paymentInstrumentLoading,
    error: paymentInstrumentError,
  } = useQuery(
    ApplicantPaymentInstrumentDisplayContainerApplicantUserPaymentInstrumentDocument,
    {
      variables: {
        applicantUserId: props.applicantUserId,
      },
    }
  );

  const {
    data: identityVerificationCaseV1Data,
    loading: identityVerificationCaseV1Loading,
    error: identityVerificationCaseV1Error,
  } = useQuery(IdVerificationProcessPaymentContainerIdentityVerificationCaseV1Document, {
    variables: { 
      identityVerificationCaseV1Id: caseId
    },
  });

  const [processPayment, { loading: processPaymentLoading }] = useMutation(
    IdVerificationProcessPaymentContainerIdentityVerificationCaseV1UpdateDocument,
    {
      onCompleted: (response) => {
        const { status } =
          response.identityVerificationCaseV1Update || {};
        if (status?.success) {
          setIsPolling(true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          message.error("Payment failed: " + status?.errorMessage);
        }
      },
      onError: (error) => {
        console.log("Error", error);
        message.error(
          "There was an error processing the payment. Please try again!"
        );
      },
      refetchQueries: [
        {
          query:
            IdVerificationProcessPaymentContainerIdentityVerificationCaseV1Document,
          variables: { caseId: caseId },
        },
      ],
    }
  );
  const [deleteIdCase] = useMutation(IdVerificationProcessPaymentContainerIdentityVerificationCaseV1DeleteDocument);

  const [updateApplicantUser] = useMutation(
    IdVerificationProcessPaymentContainerApplicantUserUpdateDocument
  );

  useProfileSubmissionCheck(applicantUserData, props.applicantUserId);

  const handleProcessPayment = async (data: IdentityVerificationCaseV1UpdateInput) => {
    const isSuccessfulTransaction = identityVerificationCaseV1Data?.identityVerificationCaseV1?.financeDetails?.transactions?.submission?.transactionReference?.isSuccess;
  
    if (isSuccessfulTransaction === true) {
      await updateApplicantUser({
        variables: {
          input: {
            id: props.applicantUserId,
            applicantUserDetails: { isProfileSubmitted: true },
          },
        },
      });
      navigate(`/applicant/cases/`);
    } else {
      await processPayment({ variables: { input: data } });
    }
  };

  const handleWithdrawRequest = async () => {
    deleteIdCase({
      variables: {
        input: {
          caseId: caseId,
        }
      },
    }).then((response) => {
      if (response.data?.identityVerificationCaseV1Delete.status.success) {
        message.success("Case withdrawn successfully");
        navigate('/applicant/onboarding')
      } else {
        message.error("Failed to withdraw case");
      }
    })
  };

  return (
    <ComponentQueryLoader
      loading={
        applicantUserLoading ||
        processPaymentLoading ||
        identityVerificationCaseV1Loading ||
        countryLoading ||
        paymentInstrumentLoading ||
        cybersourcePublicKeyLoading
      }
      error={
        applicantUserError ||
        identityVerificationCaseV1Error ||
        countryError ||
        paymentInstrumentError ||
        cybersourcePublicKeyError
      }
      hasData={
        applicantUserData &&
        identityVerificationCaseV1Data &&
        countryData &&
        paymentInstrumentData &&
        cybersourcePublicKeyData
      }
      hasDataComponent={
        isPolling ? (
          <PaymentPollingContainer
            caseId={caseId ?? ""}
            applicantUserId={props.applicantUserId ?? ""}
            isPolling={isPolling}
            setIsPolling={setIsPolling}
          />
        ) : (
          <PaymentInformation
            caseId={caseId ?? ""}
            countries={
              countryData?.countries?.eventPayload?.countries as Country[]
            }
            serviceFee={
              identityVerificationCaseV1Data?.identityVerificationCaseV1
                ?.financeDetails?.serviceFee as number
            }
            paymentInstrument={
              paymentInstrumentData?.applicantUserPaymentInstrument as PaymentInstrument
            }
            cyberSourcePublicKey={
              cybersourcePublicKeyData?.applicantUserCybersourcePublicKeyId as string
            }
            onSubmit={handleProcessPayment}
            onWithdrawRequest={handleWithdrawRequest}
          />
        )
      }
    />
  );
};

